
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';

export const useBulkEmails = () => {
  return useQuery({
    queryKey: ['bulk-emails'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bulk_emails')
        .select(`
          *,
          email_templates (
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });
};

export const useSendBulkEmail = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (emailData: {
      subject: string;
      body: string;
      recipients: any[];
      template_id?: string;
    }) => {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('User not authenticated');

      // Create bulk email record
      const { data, error } = await supabase
        .from('bulk_emails')
        .insert([{
          user_id: user.data.user.id,
          subject: emailData.subject,
          body: emailData.body,
          recipient_ids: emailData.recipients.map(r => ({ id: r.id, type: r.type, campaign_id: r.campaign_id || null })),
          recipient_count: emailData.recipients.length,
          template_id: emailData.template_id || null,
          status: 'pending',
          organization_id: currentOrganization?.id || null,
        }])
        .select()
        .single();

      if (error) throw error;

      // Trigger the Mailgun edge function to send
      await supabase.functions.invoke('send-bulk-email-mailgun', {
        body: { bulkEmailId: data.id },
      });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bulk-emails'] });
      toast({
        title: "Bulk Email Queued",
        description: `Your email has been queued for sending to ${data.recipient_count} recipients.`,
      });
    },
    onError: (error: any) => {
      console.error('Failed to send bulk email:', error);
      toast({
        title: "Error",
        description: "Failed to send bulk email. Please try again.",
        variant: "destructive",
      });
    },
  });
};
